// src/pages/finance-portal/recurring-journals/RecurringJournals.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton, Alert, CircularProgress, Skeleton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControlLabel, Switch, Checkbox, Tooltip, Tabs, Tab,
    Autocomplete, MenuItem, Select, FormControl, InputLabel,
    InputAdornment,
} from '@mui/material';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import SyncIcon from '@mui/icons-material/Sync';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

const STATUS_COLORS = {
    ACTIVE: { bg: '#dcfce7', color: '#166534' },
    PAUSED: { bg: '#fef3c7', color: '#92400e' },
    COMPLETED: { bg: '#e0e7ff', color: '#3730a3' },
    CANCELLED: { bg: '#fecaca', color: '#991b1b' },
};

const FREQUENCY_OPTIONS = [
    { value: 'DAILY', label: 'Daily' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'YEARLY', label: 'Yearly' },
];

const emptyForm = {
    name: '',
    frequency: 'MONTHLY',
    interval: 1,
    description_template: '',
    auto_reverse: false,
    start_date: '',
    end_date: '',
    lines_template: [],
};

const emptyLine = { account_id: null, debit_amount: '', credit_amount: '', description: '' };

export default function RecurringJournals() {
    const [journals, setJournals] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [processing, setProcessing] = useState(false);

    // Dialog
    const [dialog, setDialog] = useState({ open: false, editing: null });
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    // Delete
    const [deleteDialog, setDeleteDialog] = useState({ open: false, journal: null });
    const [deleting, setDeleting] = useState(false);

    const statusTabs = ['All', 'Active', 'Paused', 'Completed', 'Cancelled'];
    const statusFilter = tabValue === 0 ? '' : statusTabs[tabValue].toUpperCase();

    const fetchJournals = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            const res = await axiosInstance.get('/api/finance/recurring-journals', { params });
            if (res.data.success) {
                setJournals(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load recurring journals');
        } finally {
            setIsLoading(false);
        }
    }, [statusFilter]);

    const fetchAccounts = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
            if (res.data.success) setAccounts(res.data.data);
        } catch (err) { /* silent */ }
    }, []);

    useEffect(() => { fetchJournals(); }, [fetchJournals]);
    useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

    const getAccountLabel = (acct) => acct ? `${acct.account_number} - ${acct.name}` : '';
    const findAccount = (id) => accounts.find(a => a.id === id) || null;

    const humanReadableRRule = (j) => {
        const freq = FREQUENCY_OPTIONS.find(f => f.value === j.frequency);
        const freqLabel = freq ? freq.label.toLowerCase() : j.frequency;
        if (j.interval === 1) return `Every ${freqLabel.replace(/ly$/, '').replace(/ily$/, 'ay')}`;
        return `Every ${j.interval} ${freqLabel.replace(/ly$/, 's').replace(/ily$/, 'ays')}`;
    };

    const openDialog = (journal = null) => {
        if (journal) {
            setForm({
                name: journal.name,
                frequency: journal.frequency || 'MONTHLY',
                interval: journal.interval || 1,
                description_template: journal.description_template || '',
                auto_reverse: journal.auto_reverse || false,
                start_date: journal.start_date || '',
                end_date: journal.end_date || '',
                lines_template: journal.lines_template && journal.lines_template.length > 0
                    ? journal.lines_template.map(l => ({ account_id: l.account_id, debit_amount: l.debit_amount || '', credit_amount: l.credit_amount || '', description: l.description || '' }))
                    : [{ ...emptyLine }],
            });
        } else {
            setForm({ ...emptyForm, lines_template: [{ ...emptyLine }] });
        }
        setDialog({ open: true, editing: journal });
    };

    const updateLine = (idx, field, value) => {
        setForm(prev => {
            const lines = [...prev.lines_template];
            lines[idx] = { ...lines[idx], [field]: value };
            return { ...prev, lines_template: lines };
        });
    };

    const addLine = () => {
        setForm(prev => ({ ...prev, lines_template: [...prev.lines_template, { ...emptyLine }] }));
    };

    const removeLine = (idx) => {
        setForm(prev => ({ ...prev, lines_template: prev.lines_template.filter((_, i) => i !== idx) }));
    };

    const saveJournal = async () => {
        setSaving(true);
        const payload = {
            ...form,
            lines_template: form.lines_template.map(l => ({
                account_id: l.account_id,
                debit_amount: parseFloat(l.debit_amount) || 0,
                credit_amount: parseFloat(l.credit_amount) || 0,
                description: l.description,
            })),
        };
        try {
            if (dialog.editing) {
                await axiosInstance.put(`/api/finance/recurring-journals/${dialog.editing.id}`, payload);
                setSuccess('Recurring journal updated');
            } else {
                await axiosInstance.post('/api/finance/recurring-journals', payload);
                setSuccess('Recurring journal created');
            }
            setDialog({ open: false, editing: null });
            fetchJournals();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save recurring journal');
        } finally {
            setSaving(false);
        }
    };

    const handlePause = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/recurring-journals/${id}/pause`);
            setSuccess('Journal paused');
            fetchJournals();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to pause');
        }
    };

    const handleResume = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/recurring-journals/${id}/resume`);
            setSuccess('Journal resumed');
            fetchJournals();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to resume');
        }
    };

    const handleCancel = async (id) => {
        try {
            await axiosInstance.delete(`/api/finance/recurring-journals/${id}`);
            setSuccess('Journal cancelled');
            fetchJournals();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to cancel');
        }
    };

    const handleProcessNow = async () => {
        setProcessing(true);
        try {
            const res = await axiosInstance.post('/api/finance/recurring-journals/process');
            if (res.data.success) {
                setSuccess(`Processed ${res.data.data?.entries_created || 0} recurring journal(s)`);
            }
            fetchJournals();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to process recurring journals');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <EventRepeatIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Recurring Journals</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Automated recurring entries</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" startIcon={processing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SyncIcon />}
                            onClick={handleProcessNow} disabled={processing}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, color: '#fff', boxShadow: 'none' }}
                        >
                            Process Now
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, color: '#fff', boxShadow: 'none' }}
                        >
                            Create
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Status Tabs */}
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2,
                    '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem', minHeight: 40 },
                    '& .Mui-selected': { color: `${ACCENT} !important` },
                    '& .MuiTabs-indicator': { bgcolor: ACCENT },
                }}>
                    {statusTabs.map(t => <Tab key={t} label={t} />)}
                </Tabs>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Name', 'Frequency', 'Next Run', 'Last Run', 'Generated', 'Auto-Reverse', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4].map(i => <TableRow key={i}><TableCell colSpan={8}><Skeleton height={36} /></TableCell></TableRow>)
                                ) : journals.length === 0 ? (
                                    <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No recurring journals found</Typography>
                                    </TableCell></TableRow>
                                ) : journals.map(j => {
                                    const statusStyle = STATUS_COLORS[j.status] || {};
                                    return (
                                        <TableRow key={j.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>{j.name}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{humanReadableRRule(j)}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{j.next_run_date || '—'}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{j.last_run_date || '—'}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}>{j.generated_count || 0}</TableCell>
                                            <TableCell>
                                                {j.auto_reverse ? (
                                                    <Chip label="Yes" size="small" sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 20, bgcolor: '#e0e7ff', color: '#3730a3' }} />
                                                ) : (
                                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>No</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={j.status} size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {j.status === 'ACTIVE' && (
                                                        <Tooltip title="Pause"><IconButton size="small" onClick={() => handlePause(j.id)} sx={{ color: '#94a3b8', '&:hover': { color: '#f59e0b' } }}><PauseIcon fontSize="small" /></IconButton></Tooltip>
                                                    )}
                                                    {j.status === 'PAUSED' && (
                                                        <Tooltip title="Resume"><IconButton size="small" onClick={() => handleResume(j.id)} sx={{ color: '#94a3b8', '&:hover': { color: ACCENT } }}><PlayArrowIcon fontSize="small" /></IconButton></Tooltip>
                                                    )}
                                                    {(j.status === 'ACTIVE' || j.status === 'PAUSED') && (
                                                        <>
                                                            <Tooltip title="Edit"><IconButton size="small" onClick={() => openDialog(j)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                            <Tooltip title="Cancel"><IconButton size="small" onClick={() => handleCancel(j.id)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                                                        </>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={dialog.open} onClose={() => setDialog({ open: false, editing: null })} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    {dialog.editing ? 'Edit Recurring Journal' : 'Create Recurring Journal'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <TextField label="Name" value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                        fullWidth size="small" InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}
                    />

                    {/* RRULE Builder */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel sx={{ fontFamily }}>Frequency</InputLabel>
                            <Select value={form.frequency} onChange={e => setForm(prev => ({ ...prev, frequency: e.target.value }))}
                                label="Frequency" sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                {FREQUENCY_OPTIONS.map(f => <MenuItem key={f.value} value={f.value} sx={{ fontFamily }}>{f.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField label="Interval" value={form.interval} onChange={e => setForm(prev => ({ ...prev, interval: parseInt(e.target.value) || 1 }))}
                            size="small" type="number" sx={{ width: 100 }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' }, inputProps: { min: 1 } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                        />
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                            = Every {form.interval > 1 ? `${form.interval} ` : ''}{FREQUENCY_OPTIONS.find(f => f.value === form.frequency)?.label.toLowerCase().replace(/ly$/, form.interval > 1 ? 's' : '').replace(/ily$/, form.interval > 1 ? 'ays' : 'ay') || form.frequency}
                        </Typography>
                    </Box>

                    <TextField label="Description Template" value={form.description_template}
                        onChange={e => setForm(prev => ({ ...prev, description_template: e.target.value }))}
                        fullWidth size="small" multiline rows={2}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}
                    />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="Start Date" type="date" value={form.start_date}
                            onChange={e => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                            size="small" sx={{ flex: 1 }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        />
                        <TextField label="End Date (optional)" type="date" value={form.end_date}
                            onChange={e => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                            size="small" sx={{ flex: 1 }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        />
                    </Box>

                    <FormControlLabel
                        control={<Checkbox checked={form.auto_reverse} onChange={e => setForm(prev => ({ ...prev, auto_reverse: e.target.checked }))}
                            sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                        />}
                        label={<Typography sx={{ fontFamily, fontSize: '0.9rem' }}>Auto-reverse next period</Typography>}
                    />

                    {/* Lines Template Builder */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Journal Lines Template</Typography>
                            <Button size="small" startIcon={<AddIcon />} onClick={addLine}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '8px', color: ACCENT, fontSize: '0.8rem' }}
                            >
                                Add Line
                            </Button>
                        </Box>
                        <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'visible' }}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            {['Account', 'Debit', 'Credit', 'Description', ''].map(h => (
                                                <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {form.lines_template.map((line, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell sx={{ minWidth: 220, p: 1 }}>
                                                    <Autocomplete
                                                        options={accounts}
                                                        value={findAccount(line.account_id)}
                                                        onChange={(_, val) => updateLine(idx, 'account_id', val?.id || null)}
                                                        getOptionLabel={getAccountLabel}
                                                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                                        size="small"
                                                        renderInput={(params) => (
                                                            <TextField {...params} placeholder="Account"
                                                                InputProps={{ ...params.InputProps, sx: { fontFamily, borderRadius: '8px', fontSize: '0.85rem' } }}
                                                            />
                                                        )}
                                                        renderOption={(props, option) => (
                                                            <li {...props} key={option.id}>
                                                                <Typography sx={{ fontFamily, fontSize: '0.8rem' }}>{getAccountLabel(option)}</Typography>
                                                            </li>
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ p: 1, width: 120 }}>
                                                    <TextField value={line.debit_amount} onChange={e => updateLine(idx, 'debit_amount', e.target.value)}
                                                        size="small" type="number" placeholder="0.00"
                                                        InputProps={{ sx: { fontFamily, borderRadius: '8px', fontSize: '0.85rem' }, inputProps: { min: 0, step: '0.01' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ p: 1, width: 120 }}>
                                                    <TextField value={line.credit_amount} onChange={e => updateLine(idx, 'credit_amount', e.target.value)}
                                                        size="small" type="number" placeholder="0.00"
                                                        InputProps={{ sx: { fontFamily, borderRadius: '8px', fontSize: '0.85rem' }, inputProps: { min: 0, step: '0.01' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ p: 1 }}>
                                                    <TextField value={line.description} onChange={e => updateLine(idx, 'description', e.target.value)}
                                                        size="small" placeholder="Line description"
                                                        InputProps={{ sx: { fontFamily, borderRadius: '8px', fontSize: '0.85rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ p: 1, width: 40 }}>
                                                    {form.lines_template.length > 1 && (
                                                        <IconButton size="small" onClick={() => removeLine(idx)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                                            <RemoveCircleOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDialog({ open: false, editing: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={saveJournal}
                        disabled={saving || !form.name || !form.start_date || form.lines_template.length === 0}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : dialog.editing ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
